import { Component, OnInit, OnDestroy, HostListener, Renderer2, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { ApiServiceService } from '../../services/common/api-service.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  isMenuOpen = false;
  isProfileDropdownOpen = false;
  currentUser: any = null;
  currentUserImage: string = '';
  pendingInterests: any[] = [];
  showNotifications = false;
  notifications: any[] = [];
  hasNotificationAccess = false;
  currentPlan = 'FREE';
  unreadNotificationCount = 0;
  unreadMessageCount = 0;
  unreadInterestCount = 0;
  private unreadPollInterval: any;
  private heartbeatInterval: any;

  constructor(
    private renderer: Renderer2,
    private el: ElementRef,
    private authService: AuthService,
    private router: Router,
    private apiService: ApiServiceService
  ) { }

  get isLoggedIn(): boolean {
    return this.authService.isLoggedIn();
  }

  ngOnInit() {
    this.loadUserData();
    this.startUnreadPolling();
    this.startHeartbeat();
  }

  ngOnDestroy() {
    if (this.unreadPollInterval) {
      clearInterval(this.unreadPollInterval);
    }
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
  }

  startUnreadPolling() {
    if (!this.isLoggedIn) return;
    const profileId = localStorage.getItem('profileId');
    if (profileId) {
      this.fetchUnreadCount(profileId);
      this.fetchUnreadInterestCount(profileId);
      this.loadNotifications();
      this.unreadPollInterval = setInterval(() => {
        this.fetchUnreadCount(profileId);
        this.fetchUnreadInterestCount(profileId);
        this.loadNotifications();
      }, 10000);
    }
  }

  startHeartbeat() {
    if (!this.isLoggedIn) return;
    const profileId = localStorage.getItem('profileId');
    if (profileId) {
      // Send immediately on load
      this.apiService.sendHeartbeat(profileId).subscribe();

      // Send every 30 seconds
      this.heartbeatInterval = setInterval(() => {
        this.apiService.sendHeartbeat(profileId).subscribe({
          error: (err) => console.error('Heartbeat failed:', err)
        });
      }, 30000);
    }
  }

  fetchUnreadCount(profileId: string) {
    this.apiService.getGlobalUnreadMessageCount(profileId).subscribe({
      next: (response: any) => {
        if (response && response.status === 'success') {
          this.unreadMessageCount = response.count || 0;
        }
      },
      error: (error: any) => {
        console.error('Error fetching unread count:', error);
      }
    });
  }

  fetchUnreadInterestCount(profileId: string) {
    const sessionToken = localStorage.getItem('sessionToken');
    if (!sessionToken) return;
    this.apiService.getUnreadInterestCount(sessionToken, profileId).subscribe({
      next: (response: any) => {
        if (response && response.status === 'success') {
          this.unreadInterestCount = response.count || 0;
        }
      },
      error: (error: any) => {
        console.error('Error fetching unread interest count:', error);
      }
    });
  }

  loadUserData() {
    if (!this.isLoggedIn) return;

    const profileId = localStorage.getItem('profileId');
    const sessionToken = localStorage.getItem('sessionToken');

    if (profileId && sessionToken) {
      this.apiService.getEditDetails(sessionToken, profileId).subscribe({
        next: (response: any) => {
          if (response && response.status === 'success' && response.data) {
            this.currentUser = response.data;
            const oryIdentityId = this.currentUser.basic_info?.keycloak_id || this.currentUser.basic_info?.ory_identity_id || this.currentUser._id;
            const avatar_Id = this.currentUser.personal_info?.avatar_Id;
            if (oryIdentityId && avatar_Id) {
              const imagePath = `${oryIdentityId}/gallery/avatar/${avatar_Id}`;
              this.apiService.getImageBase64(imagePath).subscribe({
                next: (data) => {
                  this.currentUserImage = data.base64Image;
                },
                error: (error) => {
                  console.error('Error fetching header image:', error);
                }
              });
            }
          }
        },
        error: (error: any) => {
          console.error('Error fetching user profile for header:', error);
        }
      });

      this.apiService.getReceivedConnectionList(sessionToken, profileId).subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.pendingInterests = response.data.filter((req: any) => req.interest_status === 'Pending');
          }
        },
        error: (error: any) => console.error('Error fetching received connections:', error)
      });
    }
  }

  toggleNotifications() {
    this.showNotifications = !this.showNotifications;
    if (this.showNotifications) {
      this.loadNotifications();
      if (this.unreadNotificationCount > 0) {
        const sessionToken = localStorage.getItem('sessionToken');
        const profileId = localStorage.getItem('profileId');
        if (sessionToken && profileId) {
          this.apiService.markNotificationsRead(sessionToken, profileId).subscribe({
            next: () => {
              this.unreadNotificationCount = 0;
            },
            error: (err: any) => console.error('Error marking notifications read:', err)
          });
        }
      }
    }
  }

  loadNotifications() {
    const sessionToken = localStorage.getItem('sessionToken');
    const profileId = localStorage.getItem('profileId');
    if (sessionToken && profileId) {
      this.apiService.getNotifications(sessionToken, profileId).subscribe({
        next: (res: any) => {
          if (res && res.status === 'success') {
            this.hasNotificationAccess = res.has_access;
            this.currentPlan = res.current_plan || 'FREE';
            this.unreadNotificationCount = res.unread_count || 0;
            this.notifications = res.data || [];
          }
        },
        error: (err: any) => console.error('Error fetching notifications:', err)
      });
    }
  }

  getTimeAgo(dateString: string): string {
    if (!dateString) return '';
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now.getTime() - past.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay === 1) return 'Yesterday';
    return `${diffDay}d ago`;
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  toggleProfileDropdown() {
    this.isProfileDropdownOpen = !this.isProfileDropdownOpen;
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const targetElement = event.target as HTMLElement;

    if (this.isProfileDropdownOpen && !targetElement.closest('.user-profile-menu')) {
      this.isProfileDropdownOpen = false;
    }

    if (this.showNotifications && !targetElement.closest('.notification-container')) {
      this.showNotifications = false;
    }
  }
}
